import primitiveFactory from "_src/engine/factories/primitive";
import objFactory from "_src/engine/factories/object";
import { easingType, primitiveType } from "_src/engine/constants";
import { getBboxSize } from "_src/engine/math";
import minmaxUtils from "_src/engine/utility/minmax";
import intersectEvaluator from "_src/engine/intersect_evaluator";

import { vec3, vec4 } from "_src/libs/gl-matrix";

import LineShader from "_src/shaders/line/line";
import workspace from "_src/workspace";
import Animation from "_src/client/animation";
import windowManager from "_src/client/window_manager";
import renderGraph from "_src/render_graph/render_graph";
import appState from "_src/app_state";
import config from "_src/config";
import TargetControl from "_src/client/controls/target_control";

let roughGrid = null;
let preciseGrid = null;

let roughGridShader = null;
let preciseGridShader = null;

let updateDepthObject = null;
let gridCamera = null;
let workspaceCamera = null;

let axisX = null;
let axisY = null;
let axisZ = null;

let animation = null;

const SCALE_STEP = 5.0;
const DIST_FACTOR = 50.0;

const SCALE_FACTOR = 0.1;

const AXIS_LINE_LENGTH = 1;
const AXIS_LINE_WIDTH = 3;

const DEFAULT_COLOR = [0.67, 0.67, 0.67];

const GRID_LINE_WIDTH = 3;
const ROUGH_GRID_LINES_COUNT = 30;

export default {

  _scaleEnabled : true,

  init : function() {
    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const mainRenderGraph = mainWindow.renderGraph;
    const gridPass = mainRenderGraph.getPass(renderGraph.GRID);

    roughGridShader = new LineShader({useFading: true,
      lineWidth : GRID_LINE_WIDTH, transparent : true, fadeVer: DIST_FACTOR * SCALE_STEP, fadeHor: DIST_FACTOR * SCALE_STEP});
    preciseGridShader = new LineShader({useFading: true,
      lineWidth : GRID_LINE_WIDTH, transparent : true, fadeVer: DIST_FACTOR, fadeHor: DIST_FACTOR * SCALE_STEP});

    const chunks = [];

    roughGrid = objFactory.createDrawable(roughGridShader);
    roughGrid.addGeometryChunk(primitiveFactory.create(primitiveType.GRID,
        {xLines : ROUGH_GRID_LINES_COUNT, yLines : ROUGH_GRID_LINES_COUNT, xStep : SCALE_STEP, yStep : SCALE_STEP}));
    roughGrid.setDiffuseColor(DEFAULT_COLOR[0], DEFAULT_COLOR[1], DEFAULT_COLOR[2]);

    preciseGrid = objFactory.createDrawable(preciseGridShader);
    preciseGrid.addGeometryChunk(primitiveFactory.create(primitiveType.GRID,
        {xLines : ROUGH_GRID_LINES_COUNT * SCALE_STEP, yLines : ROUGH_GRID_LINES_COUNT * SCALE_STEP, xStep : 1, yStep : 1}));
    preciseGrid.setDiffuseColor(DEFAULT_COLOR[0], DEFAULT_COLOR[1], DEFAULT_COLOR[2]);

    roughGrid.setOpacity(0.0);
    preciseGrid.setOpacity(0.0);

    chunks.push.apply(chunks, roughGrid.chunks);
    chunks.push.apply(chunks, preciseGrid.chunks);

    const axisShader = new LineShader({lineWidth : AXIS_LINE_WIDTH, transparent : true});

    axisX = objFactory.createDrawable(axisShader);
    axisX.addGeometryChunk(primitiveFactory.create(primitiveType.LINE, {x1 : AXIS_LINE_LENGTH}));
    axisX.setDiffuseColor(0.8, 0.0, 0.0);
    axisX.setOpacity(0);

    axisY = objFactory.createDrawable(axisShader);
    axisY.addGeometryChunk(primitiveFactory.create(primitiveType.LINE, {y1 : AXIS_LINE_LENGTH}));
    axisY.setDiffuseColor(0.0, 0.8, 0.0);
    axisY.setOpacity(0);

    axisZ = objFactory.createDrawable(axisShader);
    axisZ.addGeometryChunk(primitiveFactory.create(primitiveType.LINE, {z1 : AXIS_LINE_LENGTH}));
    axisZ.setDiffuseColor(0.0, 0.0, 0.8);
    axisZ.setOpacity(0);

    chunks.push.apply(chunks, axisX.chunks);
    chunks.push.apply(chunks, axisY.chunks);
    chunks.push.apply(chunks, axisZ.chunks);

    for (let i = 0; i < chunks.length; ++i) {
      chunks[i].enableFrustumTest = false;
    }

    // NOTE: the order of adding does matter!

    const gridScene = gridPass.getSecondScene();

    gridScene.add(axisX);
    gridScene.add(axisY);
    gridScene.add(axisZ);

    gridScene.add(roughGrid);
    gridScene.add(preciseGrid);

    workspaceCamera = workspace.getScene().camera;
    if (config.recalculateInterfaceDepth) {
      gridCamera = gridScene.camera;
      updateDepthObject = gridPass.getMainScene().getChildren()[0];
    } else {
      gridCamera = workspaceCamera;
    }

    animation = new Animation();

    if (!appState.getStateValue("showGrid")) {
      this.hideGrid();
    }

    if (!appState.getStateValue("showGridAxes")) {
      this.hideAxes();
    }

  },

  update() {
    updateGridCamera();

    if (!this._scaleEnabled) {
      return;
    }

    this.updateDepth();

    updateGrid();
    updateAxes();
  },

  updateDepth() {
    if (config.recalculateInterfaceDepth) {
      updateDepthObject.shader.setProjectionMatrices(workspaceCamera.projMat, gridCamera.projMat);
    }
  },

  setGridColor(r, g, b) {

    roughGrid.setDiffuseColor(r, g, b);
    preciseGrid.setDiffuseColor(r, g, b);

  },

  showAxes() {

    axisX.show();
    axisY.show();
    axisZ.show();

  },

  hideAxes() {

    axisX.hide();
    axisY.hide();
    axisZ.hide();

  },

  showGrid() {

    roughGrid.show();
    preciseGrid.show();

  },

  hideGrid() {

    roughGrid.hide();
    preciseGrid.hide();

  },

  disableScale() {
    this._scaleEnabled = false;
  },

  enableScale() {
    this._scaleEnabled = true;
  },

  smoothlyShow() {

    if (animation.checkBusy()) {
      return;
    }

    animation.type = easingType.IN_OUT_SINE;
    animation.setOnUpdate((value) => {
      roughGrid.setOpacity(value);
      preciseGrid.setOpacity(value);
      axisX.setOpacity(value);
      axisY.setOpacity(value);
      axisZ.setOpacity(value);
      windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame();
    });

    animation.setOnFinish(() => {
      roughGrid.setOpacity(1);
      preciseGrid.setOpacity(1);
      axisX.setOpacity(1);
      axisY.setOpacity(1);
      axisZ.setOpacity(1);
      windowManager.getWindow(windowManager.MAIN).renderGraph.requestFrame();
    });

    animation.start(0, 1, 0.5);

  },

  updateAnimation(dt) {
    if (animation) {
      animation.update(dt);
    }
  },

  cleanup() {

    roughGrid.cleanup();
    preciseGrid.cleanup();
    axisX.cleanup();
    axisY.cleanup();
    axisZ.cleanup();

    roughGrid = null;
    preciseGrid = null;
    roughGridShader = null;
    preciseGridShader = null;
    axisX = null;
    axisY = null;
    axisZ = null;

    gridCamera = null;
    workspaceCamera = null;
    updateDepthObject = null;

    animation = null;

  }

};

const updateGridCamera = (function() {
  const bboxTmp = new Array(6);
  const sceneBBox = new Array(6);

  return function () {
    if (config.recalculateInterfaceDepth) {
      workspaceCamera.updateMatrix();

      gridCamera.setMatrix(workspaceCamera.modelMatrix);
      gridCamera.fov = workspaceCamera.fov;
      gridCamera.projectionShift[0] = workspaceCamera.projectionShift[0]
      gridCamera.projectionShift[1] = workspaceCamera.projectionShift[1]
      if (workspaceCamera.type !== gridCamera.type) {
        gridCamera.type = workspaceCamera.type;
        gridCamera.updateProjectMatrix();
      }
      if (workspaceCamera.isOrtho()) {
        gridCamera.left = workspaceCamera.left;
        gridCamera.right = workspaceCamera.right;
        gridCamera.bottom = workspaceCamera.bottom;
        gridCamera.top = workspaceCamera.top;
        gridCamera.updateProjectMatrix();
      }
    }

    const workspaceScene = windowManager.getWindow(windowManager.MAIN).renderGraph.getWorkspaceScene();
    const interfaceScene = windowManager.getWindow(windowManager.MAIN).renderGraph.getInterfaceScene();
    const transformScene = windowManager.getWindow(windowManager.MAIN).renderGraph.getTransformScene();
    minmaxUtils.reset(sceneBBox);
    minmaxUtils.expandMinmaxByScene(sceneBBox, workspaceScene);
    minmaxUtils.expandMinmaxByScene(sceneBBox, interfaceScene);
    minmaxUtils.expandMinmaxByScene(sceneBBox, transformScene);
    if (!minmaxUtils.isValid(sceneBBox)) {
      minmaxUtils.setDefaultValue(sceneBBox);
    }
    const projectionDependentViewFieldBBox = getViewFieldBBox(workspaceCamera, true, bboxTmp);
    minmaxUtils.expandMinmax(projectionDependentViewFieldBBox, sceneBBox);
    const projectionDependentUnitedBBoxSize = getBboxSize(projectionDependentViewFieldBBox);

    const sceneNear = minmaxUtils.calcNearFarByMinMax(sceneBBox, gridCamera)[0];
    const cameraNear = Math.min(sceneNear, Math.abs(gridCamera.trans[2]/2));
    gridCamera.updateNearFar(cameraNear, projectionDependentUnitedBBoxSize);
  }
})();

const updateGrid = (function() {
  const vec3Tmp = vec3.create();

  return function () {
    let viewTarget;
    const cameraControl = windowManager.getWindow(windowManager.MAIN).cameraControl;
    if (cameraControl instanceof TargetControl) {
      viewTarget = vec3.copy(vec3Tmp, cameraControl.target);
    } else {
      viewTarget = vec3.fromValues(0, 0, 0);
    }
    let distToCenter = vec3.dist(viewTarget, workspaceCamera.trans);

    setViewPoint(workspaceCamera.trans);
    setViewTarget(viewTarget);

    let power = Math.floor(Math.log(distToCenter / DIST_FACTOR) / Math.log(SCALE_STEP)) + 1;

    const scale = Math.pow(SCALE_STEP, power);
    const nextScale = scale * SCALE_STEP;

    const centerX = Math.floor(viewTarget[0] / nextScale) * nextScale;
    const centerY = Math.floor(viewTarget[1] / nextScale) * nextScale;

    preciseGridShader.setFadeDistance(DIST_FACTOR * Math.pow(SCALE_STEP, power));
    roughGridShader.setFadeDistance(DIST_FACTOR * Math.pow(SCALE_STEP, power + 1));
    roughGrid.setScale(scale, scale, scale);
    preciseGrid.setScale(scale, scale, scale);

    roughGrid.setTranslation(centerX, centerY, 0);
    preciseGrid.setTranslation(centerX, centerY, 0);
  }
})();

const updateAxes = (function() {
  const viewVector = vec3.create();
  const vec3Tmp2 = vec3.create();

  return function () {
    vec3.set(viewVector, 0, 0, -1);
    vec3.transformQuat(viewVector, viewVector, workspaceCamera.quat);
    vec3.normalize(viewVector, viewVector);

    const dist = workspaceCamera.isPerspective() ?
      vec3.length(workspaceCamera.trans) :
      workspaceCamera.top / Math.tan(0.5 * workspaceCamera.fov);

    const destVector = workspaceCamera.isPerspective() ?
      vec3.scale(vec3Tmp2, workspaceCamera.trans, -1) :
      vec3.scale(vec3Tmp2, viewVector, dist);

    const distance = Math.abs(vec3.dot(destVector, viewVector));
    const axesScale = distance * SCALE_FACTOR;

    axisX.setScale(axesScale, axesScale, axesScale);
    axisY.setScale(axesScale, axesScale, axesScale);
    axisZ.setScale(axesScale, axesScale, axesScale);
  }
})();

function setViewPoint(point) {
  roughGridShader.setViewPoint(point);
  preciseGridShader.setViewPoint(point);
}

function setViewTarget(point) {
  roughGridShader.setViewTarget(point);
  preciseGridShader.setViewTarget(point);
}

const getViewFieldBBox = (function() {

  const eye = vec3.create();
  const view = vec3.create();
  const point = vec3.create();

  const gridPlane = vec4.fromValues(0, 0, 1, 0);

  return function(object, useCameraPosition, dest) {

    const from = vec3.copy(eye, object.trans);
    const to = vec3.set(view, 0, 0, -1);
    vec3.transformQuat(to, to, object.quat);

    intersectEvaluator.rayPlaneIntersection(gridPlane, from, to, point);

    const distance = vec3.dist(from, point);

    dest[0] = point[0] - 2.0 * distance;
    dest[1] = point[1] - 2.0 * distance;
    dest[2] = Math.min(-from[2], from[2]);

    dest[3] = point[0] + 2.0 * distance;
    dest[4] = point[1] + 2.0 * distance;
    dest[5] = Math.max(-from[2], from[2]);

    return dest;

  }

})();

